import { useEffect, useState } from 'react';

/**
 * Subscribe to the browser's online/offline events. Initialises from
 * `navigator.onLine` on the first render so SSR-style mismatches
 * don't paint an initial wrong colour.
 *
 * Note: `navigator.onLine === true` only guarantees that the network
 * interface is up — not that the backend is reachable. F11.3 will add
 * a richer notion of "API is reachable" by tracking failed mutations,
 * but the browser flag is the source of truth for the badge here.
 */
export function useNetworkStatus(): { online: boolean } {
  const [online, setOnline] = useState<boolean>(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );

  useEffect(() => {
    const handleOnline = () => setOnline(true);
    const handleOffline = () => setOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return { online };
}
